import React, { useEffect, useState } from 'react';

import ApproverLevelsPanel from './ApproverLevelsPanel';

import { decimal } from 'Helpers/currencyFormat';
import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import NumberFormat from 'CommonComponents/LocalisedNumberFormat';

const DASH = '—';

const initialsOf = (level) => level.initials || (level.name ? level.name.slice(0, 2).toUpperCase() : '??');

const RECORDING_LABELS = {
    off: () => $t('Off'),
    no_approval: () => $t('On – no approval'),
    with_approval: () => $t('On – with approval'),
};

/** Read-only: follows the scope's own rule and the event's "Record all budget changes" switch. */
function RecordingChip({ recording }) {
    if (!RECORDING_LABELS[recording]) {
        return <span className="bc-dim">—</span>;
    }

    return <span className={recording === 'with_approval' ? 'bc-chip bc-chip--green' : 'bc-chip bc-chip--grey'}>{RECORDING_LABELS[recording]()}</span>;
}

export default function RuleRow({
    row,
    users,
    readOnly,
    stale,
    expanded,
    collapsed,
    onToggleExpanded,
    onToggleChildren,
    childrenCollapsed,
    hasChildren,
    onChange,
}) {
    // Cleared as soon as the row gains an approver, so the block message cannot
    // outlive the state that produced it.
    const [blockedActivation, setBlockedActivation] = useState(false);
    // Raised only by switching change control off here, not by a row that loaded
    // already off — the reader is being warned about what they just did.
    const [pendingWarning, setPendingWarning] = useState(false);
    const levels = row.levels || [];

    useEffect(() => {
        if (levels.length > 0) {
            setBlockedActivation(false);
        }
    }, [levels.length]);

    if (collapsed) {
        return null;
    }

    const isEventProfit = row.scope.type === 'event_profit';
    const isCentral = row.scope.type === 'central';
    const isCostcenter = row.scope.type === 'costcenter';
    // Event Profit covers a change with no approver by falling back to profit rather
    // than stalling it, so it is exempt from the approver requirement.
    const needsApprover = !isEventProfit && levels.length === 0;
    const noApprovers = row.active && needsApprover;
    const blockedDeactivation = !row.contingency_active && row.supports_contingency && Number(row.balance || 0) > 0;
    const pendingRequests = Number(row.pending_requests || 0);

    const money = (value) => (value === null || value === undefined ? DASH : formatToFixedMoneyWithLocale(Number(value), 2));

    // react-number-format re-emits its value whenever a prop changes it, not only
    // on typing, so an unguarded handler would mark the row dirty on first render
    // and light up Save before the user has touched anything.
    const balanceText = row.balance === null || row.balance === undefined ? '' : String(row.balance);

    // Rows that share the expanded row's tint so the group reads as one block.
    const rowClass = [isCentral ? 'bc-row--central' : '', isCostcenter ? 'bc-row--child' : '', stale ? 'bc-row--stale' : ''].filter(Boolean).join(' ');

    const toggleChangeControl = (checked) => {
        if (checked && needsApprover) {
            setBlockedActivation(true);

            return;
        }

        setBlockedActivation(false);
        setPendingWarning(!checked && pendingRequests > 0);
        onChange({ active: checked });
    };

    // Switching contingency off is the toggle that can strand money: a pending request
    // that has drawn on the pot is credited back to central once the pot is released.
    const toggleContingency = (checked) => {
        setPendingWarning(!checked && pendingRequests > 0);
        onChange({ contingency_active: checked });
    };

    const approversCell = () => {
        if (levels.length === 0) {
            return (
                <button
                    type="button"
                    className={`bc-approvers ${noApprovers ? 'bc-approvers--warn' : 'bc-approvers--muted'}`}
                    disabled={readOnly}
                    onClick={onToggleExpanded}
                >
                    {noApprovers ? $t('⚠ No approvers') : $t('＋ Add approvers')}
                    {noApprovers && <span aria-hidden="true">{expanded ? '⌃' : '⌄'}</span>}
                </button>
            );
        }

        return (
            <button type="button" className={`bc-approvers ${expanded ? 'bc-approvers--open' : ''}`} onClick={onToggleExpanded}>
                <span className="bc-avatars">
                    {levels.slice(0, 3).map((level, index) => (
                        <span key={`${level.user_id}-${index}`} className={`bc-avatar ${index % 2 === 1 ? 'bc-avatar--alt' : ''}`}>
                            {initialsOf(level)}
                        </span>
                    ))}
                </span>
                {/* Two explicit keys rather than i18next pluralisation: `count`
                    is reserved there and would look for a plural suffix on the
                    key instead of substituting the value. */}
                {levels.length === 1 ? $t('1 level') : $t('{{levels}} levels', { levels: levels.length })}
                <span aria-hidden="true">{expanded ? '⌃' : '⌄'}</span>
            </button>
        );
    };

    return (
        <>
            <tr className={rowClass}>
                <td>
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                        <div className="tw-flex tw-flex-col">
                            <span className={isCostcenter ? 'tw-text-charcoal-75' : 'tw-font-medium'}>{row.name}</span>
                            {!!row.caption && <span className="bc-help">{row.caption}</span>}
                        </div>
                        {!!hasChildren && (
                            <button type="button" className="tw-shrink-0 tw-text-charcoal-50" onClick={onToggleChildren} aria-expanded={!childrenCollapsed}>
                                <span aria-hidden="true">{childrenCollapsed ? '⌄' : '⌃'}</span>
                            </button>
                        )}
                    </div>
                </td>

                <td>
                    <label className="inbud-toggle tw-mb-0">
                        <input
                            type="checkbox"
                            className="input tw-peer"
                            checked={!!row.active}
                            disabled={readOnly}
                            aria-label={$t('Activate change control')}
                            onChange={(e) => toggleChangeControl(e.target.checked)}
                        />
                        <div className="switch tw-peer peer-checked:tw-bg-blue peer-checked:after:tw-left-[23px]"></div>
                    </label>
                </td>

                <td className={`bc-num ${row.base_budget === null || row.base_budget === undefined ? 'bc-dim' : ''}`}>
                    {formatFigure(row.base_budget, formatToFixedMoneyWithLocale, { precision: 2 })}
                </td>

                <td>
                    {!row.supports_contingency ? (
                        <span className="tw-text-charcoal-25">{DASH}</span>
                    ) : (
                        <label className="inbud-toggle tw-mb-0">
                            <input
                                type="checkbox"
                                className="input tw-peer"
                                checked={!!row.contingency_active}
                                disabled={readOnly}
                                aria-label={$t('Activate contingency')}
                                onChange={(e) => toggleContingency(e.target.checked)}
                            />
                            <div className="switch tw-peer peer-checked:tw-bg-blue peer-checked:after:tw-left-[23px]"></div>
                        </label>
                    )}
                </td>

                <td className="bc-num">
                    {!row.supports_contingency ? (
                        <span className="tw-text-charcoal-25">{DASH}</span>
                    ) : row.contingency_active && !readOnly ? (
                        <NumberFormat
                            className="bc-input tw-w-[110px]"
                            value={balanceText}
                            decimalScale={2}
                            thousandSeparator={true}
                            inputMode="decimal"
                            allowNegative={false}
                            aria-label={$t('Contingency balance')}
                            onValueChange={({ value }) => value !== balanceText && onChange({ balance: value })}
                        />
                    ) : (
                        // An inactive contingency row cannot hold a value, so the field is
                        // shown greyed and dashed rather than editable or absent.
                        <span className="bc-input bc-input--disabled tw-w-[110px]">{money(row.balance)}</span>
                    )}
                </td>

                <td className={`bc-num ${row.percent_of_base === null || row.percent_of_base === undefined ? 'bc-dim' : ''}`}>
                    {formatFigure(row.percent_of_base, (value, precision) => decimal(Number(value), precision), { precision: 1, suffix: '%' })}
                </td>

                <td>
                    <select
                        className="bc-select"
                        value={row.approval_type || 'yes_no'}
                        disabled={readOnly}
                        aria-label={$t('Approval type')}
                        onChange={(e) => onChange({ approval_type: e.target.value })}
                    >
                        <option value="yes_no">{$t('Yes / No')}</option>
                        <option value="value">{$t('Value')}</option>
                    </select>
                </td>

                <td>{approversCell()}</td>
                <td>
                    <RecordingChip recording={row.recording} />
                </td>
            </tr>

            {blockedActivation && (
                <tr className="bc-row--warn">
                    <td colSpan={9}>
                        <b>{$t('⚠ Approver required.')}</b>&nbsp;{' '}
                        {$t('Add at least one approval level before switching change control on for {{scope}}.', { scope: row.name })}
                    </td>
                </tr>
            )}

            {pendingWarning && (
                <tr className="bc-row--warn">
                    <td colSpan={9}>
                        <b>
                            {pendingRequests === 1
                                ? $t('⚠ 1 request is still pending on {{scope}}.', { scope: row.name })
                                : $t('⚠ {{pending}} requests are still pending on {{scope}}.', { pending: pendingRequests, scope: row.name })}
                        </b>
                        &nbsp; {$t('They complete under the rules they were raised under. New changes on this scope will not go through change control.')}
                    </td>
                </tr>
            )}

            {noApprovers && (
                <tr className="bc-row--warn">
                    <td colSpan={9}>
                        <b>{$t('⚠ Approver required.')}</b>&nbsp;{' '}
                        {$t(
                            'Change control is active on {{scope}} but it has no approver, so budget changes against it cannot be approved. Add at least one approval level to complete setup.',
                            { scope: row.name },
                        )}
                    </td>
                </tr>
            )}

            {blockedDeactivation && (
                <tr className="bc-row--warn">
                    <td colSpan={9}>
                        <b>{$t('⚠ Cannot deactivate the {{scope}} contingency.', { scope: row.name })}</b>&nbsp;{' '}
                        {$t(
                            'This contingency holds a balance of {{balance}}. Switch it back on, set the value to zero, save, then deactivate. The released balance of {{balance}} will transfer to Event Profit.',
                            { balance: money(row.balance) },
                        )}
                    </td>
                </tr>
            )}

            {expanded && (
                <tr className={isCentral ? 'bc-row--central' : ''}>
                    <td colSpan={9}>
                        <ApproverLevelsPanel
                            scopeName={row.name}
                            levels={levels}
                            users={users}
                            approvalType={row.approval_type}
                            threshold={row.threshold}
                            readOnly={readOnly}
                            onChange={(nextLevels) => onChange({ levels: nextLevels })}
                            onThresholdChange={(threshold) => onChange({ threshold })}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}
